import { useMemo } from 'react';

import { gql, useQuery } from '@apollo/client';

import { get as getUser } from 'js/lib/user';

import { useProductFeatures } from 'bundles/enterprise-admin/features';
import type { ProgramProductEnterpriseMetadataMultiGetQuery_EnterpriseProgramSessionAssociationsV1Resource as EnterpriseProgramSessionAssociationsV1Resource } from 'bundles/enterprise-legacy-learner-home/components/__generated__/ProgramProductEnterpriseMetadataMultiGetQuery';
import { IdTypes } from 'bundles/product-features';

export type PrivateSession = {
  productId: string;
  startsAt: number;
  endsAt: number;
};

type ProductForEnterpriseMetadata = {
  id: string;
};

const EnterpriseProgramSessionAssociationsQuery = gql`
  query EnterpriseProgramSessionAssociationsQuery(
    $productIds: [String!]!
    $skipS12nMetadata: Boolean!
    $programId: String!
  ) {
    EnterpriseProgramSessionAssociationsV1Resource {
      byProgramAndCourses(courseIds: $productIds, programId: $programId) {
        elements {
          id
          courseId
          session {
            id
            startsAt
            endsAt
          }
        }
      }
    }

    EnterpriseProgramSessionAssociationsV1Resource {
      byProgramsAndSpecializations(s12nIds: $productIds, programIds: [$programId]) @skip(if: $skipS12nMetadata) {
        elements {
          id
          baseS12nId
          session {
            id
            startsAt
            endsAt
          }
        }
      }
    }
  }
`;

type EnterpriseMetadataHookResult = {
  privateSessions?: (PrivateSession | null)[];
};

type EnterpriseProgramSessionAssociationsQueryData = {
  EnterpriseProgramSessionAssociationsV1Resource: EnterpriseProgramSessionAssociationsV1Resource;
};

type EnterpriseProgramSessionAssociationsQueryVariables = {
  productIds: string[];
  skipS12nMetadata: boolean;
  programId: string;
};

export const useEnterpriseProductMetadata = (
  programId?: string,
  products: ProductForEnterpriseMetadata[] = [],
  thirdPartyOrganizationId?: string
): EnterpriseMetadataHookResult => {
  const features = useProductFeatures({
    idType: IdTypes.Organization,
    id: thirdPartyOrganizationId || '',
  });

  const allowOrgForCurriculumBuilder = features?.features?.get('enterprise', 'allowOrgForCurriculumBuilder');
  const allowOrgForSpecializationConfiguration = features?.features?.get(
    'enterprise',
    'allowOrgForSpecializationConfiguration'
  );

  const shouldSkip = !products.length || !allowOrgForCurriculumBuilder || !getUser()?.id || !programId;

  const { data } = useQuery<
    EnterpriseProgramSessionAssociationsQueryData,
    EnterpriseProgramSessionAssociationsQueryVariables
  >(EnterpriseProgramSessionAssociationsQuery, {
    skip: shouldSkip,
    variables: {
      productIds: products.map((product) => product.id),
      skipS12nMetadata: !allowOrgForSpecializationConfiguration,
      programId: programId || '',
    },
  });

  return useMemo(() => {
    if (shouldSkip || !data) {
      return { privateSessions: [] };
    }

    const coursePrivateSessions: (PrivateSession | null)[] =
      data?.EnterpriseProgramSessionAssociationsV1Resource?.byProgramAndCourses?.elements?.map((session) =>
        session && session.session
          ? { productId: session.courseId, startsAt: session.session?.startsAt, endsAt: session.session?.endsAt }
          : null
      ) ?? [];

    const s12nPrivateSessions: (PrivateSession | null)[] =
      data?.EnterpriseProgramSessionAssociationsV1Resource?.byProgramsAndSpecializations?.elements?.map((session) =>
        session && session.baseS12nId && session.session
          ? { productId: session.baseS12nId, startsAt: session.session.startsAt, endsAt: session.session.endsAt }
          : null
      ) ?? [];

    const privateSessions = coursePrivateSessions.concat(s12nPrivateSessions);

    return { privateSessions };
  }, [data, shouldSkip]);
};

export type { ProductForEnterpriseMetadata, EnterpriseMetadataHookResult };
